import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CheckCircle2, RotateCcw, Trash2, Sparkles, Star, Pencil, CalendarDays, Clock, Users, AlertTriangle, UploadCloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { PreviewDialog } from "@/components/PreviewDialog";
import { CompletedTourView } from "@/components/CompletedTourView";
import { BottomNav } from "@/components/BottomNav";
import { usePrivacy } from "@/contexts/PrivacyContext";
import { api } from "@/lib/api";
import { statusColor, withNewTourDefaults, applyPromoDiscount, computeDerived, gratuityColor, gratuityPctColor, sumShiftHours, tourTitle } from "@/lib/fields";
import { BrandLockup } from "@/components/BrandLockup";
import { RetireBanner } from "@/components/RetireNotice";

export default function TourSheet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { financialPrivacy } = usePrivacy();
  const isNew = !id || id === "new";

  // A brand-new tour starts from defaults, optionally pre-filled by a screenshot the
  // user already uploaded on the Home screen (passed via navigation state).
  const seeded = useMemo(() => {
    if (!isNew) return null;
    const raw = location.state?.fields;
    return applyPromoDiscount(withNewTourDefaults({ ...(raw || {}) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew]);

  const [guest, setGuest] = useState(isNew ? seeded : null);
  const [profile, setProfile] = useState(isNew ? (location.state?.profile || {}) : {});
  const [loading, setLoading] = useState(!isNew);
  const saveApi = useRef(null);
  const titleInputRef = useRef(null);
  const [nameState, setNameState] = useState({ name: "", placeholder: "Name this tour" });
  const [dirty, setDirty] = useState(false);
  const [guardOpen, setGuardOpen] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);
  const [reviewHidden, setReviewHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const review = !isNew && !reviewHidden ? guest?.pending_review : null;
  const dismissReview = async () => { setReviewHidden(true); try { await api.clearTourReview(id); } catch { /* ignore */ } };

  const goBack = () => ((location.key && location.key !== "default") ? navigate(-1) : navigate("/"));
  // Where to go after the guard resolves: an explicit path (e.g. a guest profile link) or Back.
  const performLeave = () => {
    if (pendingPath) { const p = pendingPath; setPendingPath(null); navigate(p); }
    else goBack();
  };
  const attemptBack = () => { setPendingPath(null); if (dirty || isNew) setGuardOpen(true); else goBack(); };
  // Called by in-sheet links (guest profiles): guard when there are edits (or an unsaved new tour).
  const requestLeave = (path) => { if (dirty || isNew) { setPendingPath(path); setGuardOpen(true); } else navigate(path); };

  const guardSave = async () => {
    try {
      await saveApi.current?.save();
      setGuardOpen(false);
      if (!isNew) performLeave();
      else setPendingPath(null);
    } catch { /* stay on page; error toast already shown */ }
  };
  const guardDiscard = () => { setGuardOpen(false); performLeave(); };

  // Warn on hardware back / tab close while there are unsaved edits.
  useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const g = await api.getGuest(id);
      if (g && g.gifted_to) {
        // Repopulate the POC card from the recipient's saved profile so their email/phone
        // don't vanish after saving (POC contact lives on the POC's guest profile).
        try {
          const pocProf = await api.getProfile(g.gifted_to);
          if (pocProf && Object.keys(pocProf).length) g.poc_profile = pocProf;
        } catch (e) { console.warn("POC profile fetch failed:", e); }
      }
      setGuest(g);
      if (g.guest_name) {
        try { setProfile(await api.getProfile(g.guest_name)); } catch { setProfile({}); }
      }
    } catch {
      toast.error("Couldn't load that tour");
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [id, isNew, navigate]);

  useEffect(() => { load(); }, [load]);

  // Opening a tour page clears only THIS tour's "while you were away" pill (the tour is being
  // reviewed). The schedule pill and other tour pills stay until reviewed or dismissed.
  useEffect(() => { if (!isNew && id) api.dismissImportEvents({ tour_id: id }).catch(() => {}); }, [isNew, id]);

  // Collapse the tour name/stats block into a compact sticky title once the user scrolls.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 96);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSave = async (fields, prof) => {
    try {
      if (isNew) {
        const created = await api.createGuest(fields, prof);
        toast.success("Tour saved");
        navigate(`/guest/${created.id}`, { replace: true });
      } else {
        await api.updateGuest(id, fields, prof);
        await load();
        toast.success("Changes saved");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    }
  };

  const setComplete = async (complete) => {
    try {
      // Persist any unsaved inline edits BEFORE locking the sheet, so nothing is lost.
      if (complete && dirty && saveApi.current?.save) {
        await saveApi.current.save();
      }
      const updated = await api.updateGuest(id, { status_override: complete ? "Complete" : null }, {});
      setGuest(updated);
      setDirty(false);
      toast.success(complete ? "Tour marked complete" : "Tour reopened");
    } catch { toast.error("Couldn't update status"); }
  };

  const remove = async () => {
    try {
      await api.deleteGuest(id);
      navigate("/");
      toast.success("Tour moved to Trash", {
        duration: 8000,
        action: {
          label: "Undo",
          onClick: async () => {
            try { await api.restoreGuest(id); toast.success("Tour restored"); }
            catch { toast.error("Couldn't restore tour"); }
          },
        },
      });
    } catch { toast.error("Couldn't delete tour"); }
  };

  if (loading || guest === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950 text-zinc-500">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const isComplete = guest?.booking_status === "Complete";

  return (
    <div className="min-h-screen pb-28">
      {/* Sticky header + condensed title */}
      <div className="sticky top-0 z-40">
        {/* Header */}
        <div className="relative overflow-hidden border-b border-zinc-800/80 bg-zinc-950/80 px-4 py-3 backdrop-blur-xl">
          <div className="pointer-events-none absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(/logo.jpg)" }} />
          <div className="pointer-events-none absolute inset-0 plaid-veil" />
          <div className="relative mx-auto flex max-w-2xl items-center gap-3">
            <button onClick={attemptBack} className="shrink-0 rounded-full p-2 text-zinc-100 plaid-text hover:bg-black/10" data-testid="tour-back-btn">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <BrandLockup onClick={() => { sessionStorage.setItem("activeTab", "home"); requestLeave("/"); }} testid="tour-home-logo-btn" />
            <div className="flex-1" />
            {!isComplete && (
              <button
                onClick={() => saveApi.current?.uploadSheet?.()}
                disabled={extracting}
                className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-[#9B2226] px-3 py-2 font-display text-[15px] font-800 tracking-tight text-white shadow-lg ring-1 ring-black/15 transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-70 disabled:hover:scale-100"
                data-testid="header-upload-btn"
                aria-label="Upload tour sheet"
                title="Upload tour sheet"
              >
                {extracting ? <Loader2 className="h-5 w-5 animate-spin" /> : <UploadCloud className="h-5 w-5" />}
                <span>{extracting ? "Reading…" : "Upload"}</span>
              </button>
            )}
            {isComplete && !isNew && (
              <button
                onClick={() => setComplete(false)}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-full hdr-chip px-3 py-1.5 text-xs font-600 transition-opacity hover:opacity-90"
                data-testid="reopen-top-btn"
                aria-label="Reopen tour"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Reopen
              </button>
            )}
          </div>
        </div>

        {/* Condensed title — fades in once scrolled */}
        <div className={`overflow-hidden bg-zinc-950/95 backdrop-blur-xl transition-all duration-200 ${scrolled ? "max-h-14 border-b border-zinc-800/60 opacity-100" : "max-h-0 opacity-0"}`}>
          <div className="mx-auto max-w-2xl px-4 py-2.5">
            <h2 className="truncate font-display text-base font-800 tracking-tight text-zinc-50" data-testid="tour-sticky-title">
              {tourTitle(guest) || nameState.name || nameState.placeholder || "Tour"}
            </h2>
          </div>
        </div>
      </div>

      {/* Tour name + stats — placed below the plaid, on the readable page background */}
      <div className="mx-auto max-w-2xl px-4 pt-4">
        <RetireBanner onShowExport={() => { sessionStorage.setItem("activeTab", "settings"); sessionStorage.setItem("sd_focus_export", "1"); requestLeave("/"); }} />
      </div>
      <div className="mx-auto max-w-2xl px-4 pt-5" data-testid="tour-title-block">
        {isComplete ? (
          <h1 className="font-display text-2xl font-800 leading-tight tracking-tight text-zinc-50 line-clamp-2" data-testid="tour-sheet-title-static">{tourTitle(guest) || "Tour"}</h1>
        ) : (
          <div className="flex items-center gap-2">
            <input
              ref={titleInputRef}
              value={nameState.name}
              placeholder={nameState.placeholder || "Name this tour"}
              onChange={(e) => saveApi.current?.setField?.("tour_name", e.target.value)}
              className="min-w-0 flex-1 rounded-md bg-transparent px-0 font-display text-2xl font-800 tracking-tight text-zinc-50 placeholder:font-800 focus:outline-none focus:ring-0"
              data-testid="tour-sheet-title-input"
              aria-label="Tour name"
              spellCheck={false}
            />
            <button
              type="button"
              onClick={() => { const el = titleInputRef.current; if (el) { el.focus(); el.select?.(); } }}
              className="shrink-0 rounded-full p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
              data-testid="tour-name-edit-btn"
              aria-label="Edit tour name"
            >
              <Pencil className="h-4 w-4" />
            </button>
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {isNew ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-zinc-400"><Sparkles className="h-3.5 w-3.5 text-[#E0575B]" /> Tap the title to name your tour · upload a screenshot and we'll fill it in for you</span>
          ) : (
            <>
              {guest.booking_status && <span className={`rounded px-2 py-0.5 text-xs font-700 ${statusColor(guest.booking_status)}`}>{guest.booking_status}</span>}
              {(() => {
                const dc = Array.isArray(guest.tour_date) ? guest.tour_date.length : (guest.tour_date ? 1 : 0);
                return dc > 0 ? (
                  <span className="inline-flex items-center gap-1 text-sm font-600 text-zinc-300" data-testid="tour-header-days">
                    <CalendarDays className="h-4 w-4 text-[#E0575B]" /> {dc} day{dc > 1 ? "s" : ""}
                  </span>
                ) : null;
              })()}
              {(() => {
                const th = sumShiftHours(guest.day_shifts) || Number(guest.total_hours) || (isComplete ? 7 : 0);
                return th > 0 ? (
                  <span className="inline-flex items-center gap-1 text-sm font-600 text-zinc-300" data-testid="tour-header-hours">
                    <Clock className="h-4 w-4 text-zinc-400" /> {th} hrs
                  </span>
                ) : null;
              })()}
              {Number(guest.party_size) > 0 && (
                <span className="inline-flex items-center gap-1 text-sm font-600 text-zinc-300" data-testid="tour-header-guests" aria-label={`${guest.party_size} guests`}>
                  <Users className="h-4 w-4 text-emerald-400" /> {guest.party_size}
                </span>
              )}
              {Number(guest.rating) > 0 && (
                <span className="inline-flex items-center gap-0.5" data-testid="tour-header-rating" aria-label={`Rating ${guest.rating} of 5`}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`h-4 w-4 ${n <= Number(guest.rating) ? "fill-amber-400 text-amber-400" : "text-zinc-600"}`} />
                  ))}
                </span>
              )}
              {!financialPrivacy && (guest.gratuity !== undefined && guest.gratuity !== null && guest.gratuity !== "") && (() => {
                const tipPct = guest.tip_percent ?? computeDerived(guest).tip_percent;
                return (
                  <span className="inline-flex items-center gap-1 text-sm font-700" data-testid="tour-header-tip">
                    <span className={gratuityColor(tipPct)}>${Number(guest.gratuity).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                    {tipPct !== null && tipPct !== undefined && tipPct !== "" && (
                      <span className={gratuityPctColor(tipPct)}>({tipPct}%)</span>
                    )}
                  </span>
                );
              })()}
            </>
          )}
        </div>
      </div>


      {review && !isComplete && ((review.fields && review.fields.length > 0) || review.name) && (
        <div className="mx-auto max-w-2xl px-4 pt-4">
          <div className="rounded-2xl border border-amber-500/40 bg-amber-500/[0.08] p-4" data-testid="tour-review-card">
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500/20 text-amber-300"><AlertTriangle className="h-5 w-5" /></span>
              <div className="min-w-0 flex-1">
                <h3 className="font-display text-sm font-700 tracking-tight text-amber-200">Please review this import</h3>
                <p className="mt-0.5 text-xs text-amber-200/70">A shared screenshot updated this tour — double-check these before your day.</p>
                <ul className="mt-2 space-y-1.5">
                  {(review.fields || []).map((f) => {
                    const simple = f.simple || f.key === "guest_list" || typeof f.old === "object" || typeof f.new === "object";
                    return (
                    <li key={f.key} className="text-xs text-zinc-200" data-testid={`review-field-${f.key}`}>
                      <span className="font-600 text-zinc-100">{f.label}:</span>{" "}
                      {simple ? (
                        <span className="font-600 text-emerald-300">updated</span>
                      ) : (
                        <>
                          <span className="text-zinc-500 line-through">{String(f.old ?? "—")}</span>
                          <span className="mx-1 text-zinc-500">→</span>
                          <span className="font-600 text-emerald-300">{String(f.new ?? "—")}</span>
                        </>
                      )}
                    </li>
                    );
                  })}
                  {review.name && (
                    <li className="text-xs leading-relaxed text-zinc-200" data-testid="review-name">
                      <span className="font-600 text-zinc-100">Guest name:</span>{" "}
                      the sheet says <span className="font-600 text-amber-200">"{review.name.sheet}"</span> but it's saved as <span className="font-600 text-zinc-100">"{review.name.saved}"</span>. Confirm it's the same guest (possible returning guest).
                    </li>
                  )}
                </ul>
                <button onClick={dismissReview} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-500/20 px-3 py-1.5 text-xs font-700 text-amber-200 transition-colors hover:bg-amber-500/30" data-testid="review-dismiss-btn">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Looks good
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Sheet: clean read-only view when marked complete, editable otherwise */}
      <div className="mx-auto max-w-2xl px-4 pb-5 pt-4">
        {isComplete ? (
          <CompletedTourView guest={guest} financialPrivacy={financialPrivacy} />
        ) : (
        <PreviewDialog
          inline
          open
          initialFields={guest}
          initialProfile={profile}
          returnMatch={isNew ? location.state?.return_match : null}
          onSave={handleSave}
          onDirtyChange={setDirty}
          saveApiRef={saveApi}
          onTourNameState={setNameState}
          onExtractingChange={setExtracting}
          onRequestLeave={requestLeave}
          newTour={isNew}
          saveLabel={isNew ? "Save tour" : "Save changes"}
        />
        )}

        {!isNew && (
          <div className="mt-8 space-y-3">
            {isComplete ? (
              <div className="flex items-center justify-between rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3" data-testid="complete-banner">
                <span className="flex items-center gap-2 text-sm text-emerald-300"><CheckCircle2 className="h-5 w-5" /> Marked complete</span>
                <Button variant="ghost" onClick={() => setComplete(false)} className="text-emerald-300 hover:bg-emerald-500/10" data-testid="reopen-btn">
                  <RotateCcw className="mr-1.5 h-4 w-4" /> Reopen
                </Button>
              </div>
            ) : (
              <Button onClick={() => setComplete(true)} className="w-full bg-emerald-600 py-6 text-base text-white hover:bg-emerald-700" data-testid="mark-complete-btn">
                <CheckCircle2 className="mr-2 h-5 w-5" /> Mark Tour Complete
              </Button>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="mx-auto flex items-center gap-1.5 text-sm text-red-400/80 hover:text-red-400" data-testid="delete-tour-btn">
                  <Trash2 className="h-4 w-4" /> Delete this tour
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="border-zinc-800 bg-zinc-950" data-testid="delete-tour-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-zinc-100">Delete this tour?</AlertDialogTitle>
                  <AlertDialogDescription className="text-zinc-400">This moves the tour to Trash. You can restore it from Settings within 30 days.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100" data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={remove} className="bg-red-600 text-white hover:bg-red-700" data-testid="delete-confirm-btn">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      <AlertDialog open={guardOpen} onOpenChange={setGuardOpen}>
        <AlertDialogContent className="border-zinc-800 bg-zinc-950" data-testid="unsaved-guard-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-100">You have unsaved changes</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Do you want to save your changes before leaving this tour sheet?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100" data-testid="guard-stay-btn">Stay</AlertDialogCancel>
            <button onClick={guardDiscard} className="inline-flex items-center justify-center rounded-md border border-red-500/40 bg-transparent px-4 py-2 text-sm font-500 text-red-300 hover:bg-red-500/10" data-testid="guard-discard-btn">Discard</button>
            <AlertDialogAction onClick={guardSave} className="bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="guard-save-btn">Save</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {!dirty && !isNew && <BottomNav value="" onChange={(t) => { sessionStorage.setItem("activeTab", t); requestLeave("/"); }} />}
    </div>
  );
}
